import React, { useState } from "react";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  MARKET_CONTEXT_BAR_COVERAGE_CAP_LABEL,
  MARKET_CONTEXT_BAR_COVERAGE_ROWS,
  MARKET_CONTEXT_COVERAGE_SESSION_COLUMNS,
  marketContextCoverageCell,
} from "../../../../utils/marketContextBarCoverage";

const BAR_COVERAGE_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 whitespace-normal";

function MarketContextBarCoverageTable(): React.ReactElement {
  return (
    <div data-shadcn-data-table="" className="w-full min-w-0 overflow-x-auto">
      <Table className="min-w-[44rem]">
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap text-[color:var(--brand-tropical-surf)]">
              Timeframe
            </TableHead>
            {MARKET_CONTEXT_COVERAGE_SESSION_COLUMNS.map((col) => (
              <TableHead
                key={col.key}
                className="whitespace-nowrap text-[color:var(--brand-tropical-surf)]"
              >
                <span className="hidden lg:inline">{col.label}</span>
                <span className="lg:hidden">{col.shortLabel}</span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {MARKET_CONTEXT_BAR_COVERAGE_ROWS.map((row) => (
            <TableRow
              key={row.timeframe}
              className={row.longHorizon ? "text-muted-foreground" : undefined}
            >
              <TableCell className="whitespace-nowrap font-medium">{row.timeframe}</TableCell>
              {MARKET_CONTEXT_COVERAGE_SESSION_COLUMNS.map((col) => (
                <TableCell key={col.key} className="whitespace-nowrap">
                  {marketContextCoverageCell(row, col.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Shared body for the modal dialog and the in-wizard slide panel. */
export function MarketContextBarCoverageContent(): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <MarketContextBarCoverageTable />
      <Field>
        <FieldDescription className={BAR_COVERAGE_FIELD_DESC_CLASS}>
          4h and 1d rows show rounded upper bounds. At those sizes,{" "}
          {MARKET_CONTEXT_BAR_COVERAGE_CAP_LABEL} bars can span decades to centuries. Most reports use
          a much shorter date range.
        </FieldDescription>
        <FieldDescription className={`${BAR_COVERAGE_FIELD_DESC_CLASS} mt-2`}>
          International symbols use their local exchange hours; coverage is similar to US extended
          hours or shorter.
        </FieldDescription>
      </Field>
    </div>
  );
}

export function MarketContextBarCoverageHeading(): React.ReactElement {
  return (
    <div className="space-y-1">
      <p className="m-0 font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
        Bar coverage at {MARKET_CONTEXT_BAR_COVERAGE_CAP_LABEL} candles
      </p>
      <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-muted-foreground">
        Approximate maximum history at each candle size and session type (at the{" "}
        {MARKET_CONTEXT_BAR_COVERAGE_CAP_LABEL}-bar cap).
      </p>
    </div>
  );
}

export function MarketContextBarCoverageDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        data-market-context-bar-coverage-dialog=""
        className="flex max-h-[min(90vh,44rem)] flex-col sm:max-w-4xl"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
            Bar coverage at {MARKET_CONTEXT_BAR_COVERAGE_CAP_LABEL} candles
          </DialogTitle>
          <DialogDescription>
            Approximate maximum history at each candle size and session type (at the{" "}
            {MARKET_CONTEXT_BAR_COVERAGE_CAP_LABEL}-bar cap).
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pe-1">
          <MarketContextBarCoverageContent />
        </div>

        <DialogFooter className="shrink-0 sm:justify-end" showCloseButton />
      </DialogContent>
    </Dialog>
  );
}

/** Inline link after timeframe help — opens a dialog, or calls `onOpen` when provided. */
export function MarketContextBarCoverageTrigger(props: {
  onOpen?: () => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const useInlineOpen = typeof props.onOpen === "function";

  return (
    <>
      <button
        type="button"
        data-market-context-bar-coverage-trigger=""
        onClick={() => {
          if (useInlineOpen) {
            props.onOpen?.();
            return;
          }
          setOpen(true);
        }}
        className="inline-flex w-fit max-w-full items-center gap-1.5 text-left font-[family-name:var(--font-primary)] text-sm font-normal !text-[color:var(--brand-tropical-surf)] underline decoration-[color:var(--brand-tropical-surf)]/35 underline-offset-[3px] transition-[color,text-decoration-color] hover:decoration-[color:var(--brand-tropical-surf)]"
      >
        <Info
          className="size-3.5 shrink-0 !text-[color:var(--brand-tropical-surf)]"
          stroke="currentColor"
          aria-hidden
        />
        <span>View bar coverage by timeframe and session</span>
      </button>
      {useInlineOpen ? null : (
        <MarketContextBarCoverageDialog open={open} onOpenChange={setOpen} />
      )}
    </>
  );
}
